import {
  Language,
  Service,
  View as ViewData,
  ViewMeta,
} from '@yleisradio/areena-types';
import { FavoriteUpdate } from 'components/FavoriteUpdate';
import { HeaderImageAspectRatio } from 'components/Header/types';
import { Metadata, MetaImageAspectRatio } from 'components/Metadata';
import { View } from 'components/View';
import { GetServerSideProps } from 'next';
import React, { FunctionComponent, useMemo } from 'react';
import { fetchSlugs, getView } from 'services/areena-api/fetchers';
import { clientPathToViewPath, ViewPathTypes } from 'services/areena-api/path';
import { filterViewByAudience } from 'utils/content';
import {
  getTodaysEpgDate,
  getEpgDateBoundaries,
  isEpgDateInFuture,
} from 'utils/epg-date';
import { getItemService, isRadioItem } from 'utils/item';
import { isAuthenticated } from 'utils/login';
import { getItemRedirect, getPersonalPageRedirect } from 'utils/redirects';
import { AnalyticsContextProvider } from 'contexts/AnalyticsContext';
import { PlayerStateProvider } from 'contexts/PlayerStateContext';
import { LocationContextProvider } from 'contexts/LocationContext';
import { ProgressCacheInvalidatorProvider } from 'contexts/ProgressCacheInvalidatorContext';
import { Guide } from 'components/Guide';
import { useRouter } from 'next/router';
import { ReferrerProvider } from 'contexts/ReferrerContext';
import { getSchemaOrgNode } from 'services/programs-api/fetchers';
import { SchemaOrgNode } from 'services/schema-org/types';
import dynamic from 'next/dynamic';
import { isPackageViewPathWithSlug, serviceFromClientPath } from 'utils/url';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';

type Props = {
  epgDate: string;
  firstDateOfEpg: string;
  lastDateOfEpg: string;
  isEpgOfFutureDate: boolean;
  isEpgOfToday: boolean;
  isViewMenuEnabled: boolean;
  headerImageAspectRatio: HeaderImageAspectRatio;
  meta: ViewMeta;
  path: string;
  userIsAuthenticated: boolean;
  view: ViewData;
  viewType: ViewPathTypes;
  selectedTab: string | null;
  metaImageAspectRatio: MetaImageAspectRatio;
  referrer: string | null;
  schemaOrgNode: SchemaOrgNode | null;
  slugs: Record<string, string> | null;
  areenaService: Service | null;
};

const Player = dynamic(() => import('../components/Player'));

const Page: FunctionComponent<Props> = ({
  epgDate,
  firstDateOfEpg,
  lastDateOfEpg,
  isEpgOfFutureDate,
  isEpgOfToday,
  isViewMenuEnabled,
  headerImageAspectRatio,
  meta,
  path,
  userIsAuthenticated,
  view,
  viewType,
  selectedTab,
  metaImageAspectRatio,
  referrer,
  schemaOrgNode,
  slugs,
  areenaService,
}) => {
  const { query } = useRouter();

  const filteredViewData = useMemo(
    () => filterViewByAudience(view, userIsAuthenticated),
    [view, userIsAuthenticated]
  );
  const isGuideView = viewType === 'guide';

  const { setAreenaService } = useAreenaService();

  useIsomorphicLayoutEffect(() => {
    setAreenaService(areenaService ?? undefined);
  }, [areenaService, setAreenaService]);

  const getViewElement = () => {
    switch (viewType) {
      case 'guide':
        return (
          <Guide
            currentDate={epgDate}
            firstDate={firstDateOfEpg}
            lastDate={lastDateOfEpg}
            isFutureDate={isEpgOfFutureDate}
            isViewMenuEnabled={isViewMenuEnabled}
            isToday={isEpgOfToday}
            view={filteredViewData}
          />
        );
      case 'view':
        return (
          <View
            headerImageAspectRatio={headerImageAspectRatio}
            isViewMenuEnabled={isViewMenuEnabled}
            view={filteredViewData}
            viewKey={path}
            selectedTab={selectedTab}
          />
        );
    }
  };

  const primaryPlayControl = view.header?.controls?.find(
    (c) => c.tag === 'play'
  );

  return (
    <>
      <Metadata
        path={path}
        query={query}
        schemaOrgNode={schemaOrgNode}
        view={view}
        viewMeta={meta}
        guideDate={isGuideView ? epgDate : null}
        isTodaysGuide={isGuideView && isEpgOfToday}
        metaImageAspectRatio={metaImageAspectRatio}
        slugs={slugs}
      />
      <FavoriteUpdate item={meta.item} />
      <ReferrerProvider referrer={referrer || undefined}>
        <AnalyticsContextProvider context={view.analytics?.context?.comscore}>
          <ProgressCacheInvalidatorProvider>
            <LocationContextProvider>
              <PlayerStateProvider>
                {getViewElement()}
                <Player primaryPlayControl={primaryPlayControl} />
              </PlayerStateProvider>
            </LocationContextProvider>
          </ProgressCacheInvalidatorProvider>
        </AnalyticsContextProvider>
      </ReferrerProvider>
    </>
  );
};

export default Page;

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale,
  req: {
    cookies,
    headers: { cookie: cookieHeader, referer: referrer = null },
  },
  res,
  resolvedUrl,
}) => {
  const featureNavigation2024Enabled =
    process.env.FEATURE_NAVIGATION_2024 === 'true';

  const {
    pathname: path,
    search: query,
    searchParams: queryParams,
  } = new URL(resolvedUrl, 'https://localhost/');
  const language: Language = locale === 'sv' ? 'sv' : 'fi';

  const personalPageRedirect = getPersonalPageRedirect(
    path,
    language,
    featureNavigation2024Enabled
  );

  if (personalPageRedirect) {
    return redirectWithQuery(personalPageRedirect, query);
  }

  const userSuppliedEpgDate = getEpgDateFromQueryParams(queryParams);
  const todaysEpgDate = getTodaysEpgDate();
  const epgDate = userSuppliedEpgDate || todaysEpgDate;

  const service = serviceFromClientPath(path);

  const slugs = isPackageViewPathWithSlug(path)
    ? await fetchSlugs(service, language).then((res) => res?.data)
    : undefined;

  const apiPath = clientPathToViewPath(path, {
    epgDate,
    featureNavigation2024Enabled,
    slugs: slugs,
  });
  const itemId = getItemIdFromClientPath(path);

  if (apiPath) {
    const [apiResponse, schemaOrgNode] = await Promise.all([
      getView({
        path: apiPath.path,
        language,
        requestCookie: cookieHeader,
      }),
      itemId ? getSchemaOrgNode(itemId, language) : null,
    ]);

    if (apiResponse) {
      const { cacheControl, setCookieHeaders, meta, view } = apiResponse;
      const { item } = meta;

      if (cacheControl) {
        res.setHeader('Cache-Control', cacheControl);
      }
      if (setCookieHeaders) {
        res.setHeader('Set-Cookie', setCookieHeaders);
      }

      const pageRedirect = getItemRedirect(
        path,
        language,
        meta,
        view.presentation
      );

      if (pageRedirect) {
        return redirectWithQuery(pageRedirect, query);
      }

      res.setHeader('Vary', 'Cookie');

      const isPodcast = item && isRadioItem(item);
      const headerImageAspectRatio = isPodcast ? '1:1' : '16:9';
      const metaImageAspectRatio = isPodcast ? '1:1' : '16:9';

      const epgDateBoundaries = getEpgDateBoundaries();
      const selectedTab = queryParams.get('t');

      const areenaService =
        getAreenaService(path) ?? (item && getItemService(item));

      return {
        props: {
          viewType: apiPath.type,
          view,
          meta,
          path,
          userIsAuthenticated: isAuthenticated(cookies),
          headerImageAspectRatio,
          epgDate,
          firstDateOfEpg: epgDateBoundaries.first,
          lastDateOfEpg: epgDateBoundaries.last,
          isEpgOfFutureDate: isEpgDateInFuture(epgDate),
          isEpgOfToday: epgDate === todaysEpgDate,
          isViewMenuEnabled: featureNavigation2024Enabled,
          selectedTab,
          metaImageAspectRatio,
          referrer,
          schemaOrgNode,
          slugs: slugs ?? null,
          areenaService: areenaService ?? null,
        },
      };
    }
  }

  return { notFound: true };
};

function redirectWithQuery(path: string, query: string) {
  return { redirect: { destination: `${path}${query}`, permanent: true } };
}

function getEpgDateFromQueryParams(
  queryParams: URLSearchParams
): string | null {
  return queryParams.get('t');
}

function getItemIdFromClientPath(path: string): string | null {
  const [, id] = /\/(1-\d+)/.exec(path) || [];
  return id || null;
}

function getAreenaService(path: string): Service | null {
  if (path.startsWith('/tv')) return 'tv';

  if (path.startsWith('/podcastit') || path.startsWith('/poddar'))
    return 'radio';

  return null;
}
